import type { ColumnDef } from '@tanstack/react-table';

import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { LockCloseIcon, LockOpenIcon, UserIcon } from '@/icons';
import { SyncIcon } from '@/icons/sync';
import { formatDate } from '@/lib/dateFormatter';
import { BasicStores, StoreDeletePayload, ToggleStatusPayload } from '@/services/basicStores/basicStores.types';
import { Link } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { Button } from 'ui/button';
type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

type OpenConfirmationProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => void | Promise<void>;
  onCancel: () => void;
}) => void;

export function getColumns(
  mutate: (payload: ToggleStatusPayload) => Promise<unknown>,
  deleteStore: (payload: StoreDeletePayload) => Promise<{ message: string }>,
  openConfirmation: OpenConfirmationProps,
  t: ReturnType<typeof useDynamicLocale>['t'],
): ColumnDef<BasicStores>[] {
  return [
    {
      id: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium truncate max-w-[10rem]">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'storeNameEng',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.storeInfo')} />,
      cell: ({ row }) => {
        const storeName = row.original.storeName;
        const storeNameEng = row.original.storeNameEng;
        const storeImage = row.original.clubLogo;
        const clubId = row.original.clubId;

        return (
          <div className="flex items-center space-x-3 max-w-[20rem] truncate">
            <img
              src={storeImage?.path}
              alt={storeImage?.alt ?? ''}
              className="w-8 h-8 rounded-full object-cover shrink-0"
            />

            <div className="truncate">
              {row.getValue('status') === 'B' ? (
                <div className="font-medium text-xs text-link underline cursor-not-allowed">{storeNameEng}</div>
              ) : (
                <Can
                  rule={{ allOf: [PERMS.club.basic.editView] }}
                  fallback={<div className="font-medium text-xs text-foreground-subtle underline">{storeNameEng}</div>}
                >
                  <Link
                    to="/store-information/basic-stores/$manage-store"
                    params={{ 'manage-store': clubId }}
                    search={(previous) => ({
                      createdDate: previous.createdDate,
                      breadcrumbReturnTo: window.location.href,
                    })}
                  >
                    <div className="font-medium text-xs text-link underline">{storeNameEng}</div>
                  </Link>
                </Can>
              )}

              <div className="text-foreground-subtle font-medium text-xs">{storeName}</div>
            </div>
          </div>
        );
      },

      enableHiding: false,
    },
    {
      accessorKey: 'telephone',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.telephone')} />,
      cell: ({ row }) => <div className="text-xs font-medium">{row.getValue('telephone')}</div>,
      enableHiding: false,
    },
    {
      accessorKey: 'prefecture',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.location')} />,
      cell: ({ row }) => <div className="text-xs font-medium">{row.getValue('prefecture')}</div>,
      enableHiding: false,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.status')} />,
      cell: ({ row }) => {
        return (
          <div className="font-medium">
            {row.getValue('status') === 'A' ? (
              <div className="text-success text-xs font-medium">
                <Translation id={'common.page.table.active'} />
              </div>
            ) : (
              <div className="text-destructive-text text-xs font-medium">
                <Translation id={'common.page.table.blocked'} />
              </div>
            )}
          </div>
        );
      },
      enableHiding: false,
      enableColumnFilter: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdDate')} />
      ),

      cell: ({ row }) => {
        return (
          <div className="font-medium whitespace-nowrap text-xs">
            {formatDate(Number(row.getValue('createdDate')), 'YYYY/MM/DD HH:mm:ss')}
          </div>
        );
      },

      enableHiding: false,
      enableColumnFilter: true,
    },
    {
      accessorKey: 'updatedDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.updatedDate')} />
      ),
      cell: ({ row }) => {
        return (
          <div className="font-medium whitespace-nowrap text-xs">
            {formatDate(Number(row.getValue('updatedDate')), 'YYYY/MM/DD HH:mm:ss')}
          </div>
        );
      },
      enableHiding: false,
    },
    {
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.action')} />,
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          {row.getValue('status') === 'A' && (
            <Can rule={{ allOf: [PERMS.host.basic.list] }}>
              <Link
                to="/store-information/basic-stores/host-management/$clubid"
                params={{ clubid: row.original.clubId }}
                search={{ breadcrumbReturnTo: window.location.href }}
              >
                <UserIcon className="text-border-strong cursor-pointer" />
              </Link>
            </Can>
          )}
          <Can rule={{ allOf: [PERMS.club.basic.statusUpdate] }}>
            <Button
              variant={'ghost'}
              className="p-0 m-0"
              onClick={() => {
                openConfirmation({
                  title: t('common.page.modal.confirm'),
                  description:
                    row.getValue('status') === 'A'
                      ? t('storeManagement.form.blockModel.description')
                      : t('storeManagement.form.unblockModel.description'),
                  cancelLabel: t('common.page.modal.no'),
                  actionLabel: t('common.page.modal.submit'),
                  onAction: async () => {
                    await mutate({
                      clubId: row.original.clubId,
                      status: row.getValue('status') === 'A' ? 'B' : 'A',
                    });
                  },
                  onCancel: () => {},
                  size: 'lg',
                  variant: 'warning',
                });
              }}
            >
              {row.getValue('status') === 'A' ? (
                <LockCloseIcon className="text-destructive-text size-4" />
              ) : (
                <LockOpenIcon className="text-link size-4" />
              )}
            </Button>
          </Can>
          {row.getValue('status') === 'A' && (
            <Can rule={{ allOf: [PERMS.club.basic.delete] }}>
              <Trash2
                size={16}
                onClick={() => {
                  openConfirmation({
                    title: t('common.page.modal.confirm'),
                    description: t('storeManagement.form.deleteModel.description', {
                      storeName: row.original.storeNameEng,
                    }),
                    cancelLabel: t('common.page.modal.no'),
                    actionLabel: t('common.page.modal.submit'),
                    onAction: async () => {
                      await deleteStore({
                        clubId: row.original.clubId,
                      });
                    },
                    onCancel: () => {},
                    size: 'lg',
                    variant: 'destructive',
                  });
                }}
                className="cursor-pointer text-destructive-text hover:text-destructive-text ml-2"
              />
            </Can>
          )}
          {row.getValue('status') === 'A' && (
            <Can rule={{ allOf: [PERMS.club.switch] }}>
              <Link to="/store-information/basic-stores/switch-store/$id" params={{ id: row.original.clubId }}>
                <SyncIcon className="w-4 h-4 text-warning cursor-pointer" />
              </Link>
            </Can>
          )}
        </div>
      ),
      size: 40,
    },
  ];
}
